import { getRouterToFromIndexedObject } from "@/helpers/Router.js";
import { buildArtistListForSearchResults } from "@/helpers/captionHelpers";

/**
 * Gets the object number for the search result item
 *
 * @param {object} result - A search result
 * @return {string} the object number
 */
export function getObjectNumberFromSearchResult(result) {
  let num;
  if (result.object_number != undefined) {
    num = result.object_number;
  }
  return num;
}

/**
 * Gets the role for the search result item
 * Refactor this with DEV-8235?
 * @param {object} result - A search result
 * @param {string} personIndexedId - the producer's indexedId
 * @param {function} translate - the translate function
 *
 * @return {array} Array of caption entries
 */
export function getRoleFromSearchResult(result, personIndexedId, translate) {
  if (result == undefined) {
    return {};
  }

  if (personIndexedId == undefined || personIndexedId == "") {
    return;
  }
  let producers = result.producers || [];
  let previous_producers = result.previous_producers || [];

  let roles = [];
  producers.forEach(function (producer) {
    if (
      producer.id != undefined &&
      producer.id.indexOf(personIndexedId) > -1 &&
      producer.role != undefined
    ) {
      roles = producer.role;
    }
  });

  previous_producers.forEach(function (producer) {
    if (producer.id != undefined && producer.id.indexOf(personIndexedId) > -1) {
      roles = [translate("image_and_caption.previous_role")];
    }
  });

  return translate("image_and_caption.role", { roles: roles.join(",") });
}

/**
 * Transforms a search result into the format needed for the Thumbnail view
 *
 * @param {Object} result - a search result object from ElasticSearch
 * @param {function} translate - the translate function
 * @param {Object} params - extra configuration params
 *
 * @returns {object} - an object with a {heading, subheading, to (route), imageUuid, imageAlt}
 */
export function transformSearchResultToThumbnailItemWithRole(
  result,
  translate,
  params
) {
  let indexedId = undefined;
  if (result == undefined) {
    return {};
  }

  if (params && params.constituentIndexedId) {
    indexedId = params.constituentIndexedId;
  }
  return transformSearchResultToThumbnailItem(
    result,
    getRoleFromSearchResult(result, indexedId, translate)
  );
}

/**
 * Transforms a search result into the format needed for the Thumbnail view
 *
 * @param {Object} result - a search result object from ElasticSearch
 * @param {function} translate - the translate function
 *
 * @returns {object} - an object with a {heading, subheading, to (route), imageUuid, imageAlt}
 */
export function transformSearchResultToThumbnailItemWithArtist(
  result,
  translate
) {
  if (result == undefined) {
    return {};
  }

  // use the helper to construct the artists list
  let artists = buildArtistListForSearchResults(result.producers, translate);

  return transformSearchResultToThumbnailItem(result, artists.join("; "));
}

/**
 * Transforms a search result into the format needed for the Thumbnail view
 *
 * @param {Object} result - a search result object from ElasticSearch
 * @param {String} subheading - the subheading (artist)
 *
 * @returns {object} - an object with a {heading, subheading, to (route), imageUuid, imageAlt}
 */
export function transformSearchResultToThumbnailItem(result, subheading) {
  let artists = [];
  if (result == undefined) {
    return {};
  }

  // add the producers / artists
  if (result.producers) {
    result.producers.forEach((producer) => {
      artists.push(producer.primary_name);
    });
  }

  // setup the default item
  let item = {
    heading: result.primary_name,
    subheading: subheading,
    to: getRouterToFromIndexedObject(result),
    variableImageHeight: true
  };

  // add extra classes
  if (result.extraClasses) {
    item.classes = result.extraClasses;
  }

  // add image properties
  let manifest = result.manifest;
  if (manifest?.thumbUuid) {
    item.image = {
      id: manifest.thumbUuid,
      alt: manifest.altText ?? result.primary_name
    };
  }

  // add search cartouche properties
  item.is_parent = result.is_parent;
  if (result.parent_tree) {
    item.parent_tree = result.parent_tree;
  }
  if (result.manuscript_number) {
    item.manuscript_number = result.manuscript_number;
  }

  if (result?.is_deaccessioned == true) {
    item.is_deaccessioned = true;
  }

  return item;
}

/**
 * Gets the metadata caption for the search grid result item
 *
 * @param {object} result - An elasticsearch search result object
 * @return {array} Array of caption entry strings
 */
export function getSearchGridMetadata(result) {
  let final = [];

  // add producer names
  if (result.producers) {
    let producerStrings = [];

    result.producers.forEach((producer) => {
      if (producer != undefined) {
        if (producer.prefix) {
          producerStrings.push(producer.prefix + " " + producer.primary_name);
        } else {
          producerStrings.push(producer.primary_name);
        }
      }
    });
    final = [...final, producerStrings.join(", ")];
  }

  // add culture
  if (result.culture != undefined) {
    final = [...final, ...result.culture];
  }

  // add date
  if (result.date_created != undefined) {
    if (Array.isArray(result.date_created)) {
      final.push(result.date_created[0]);
    } else {
      final.push(result.date_created);
    }
  }

  return final;
}

/**
 * Gets the metadata for the search result list card item
 *
 * @param {object} result - An elasticsearch search result object
 * @return {object} An object with metadata fields
 */
export function getSearchListMetadata(result) {
  // get artists/producers
  let producer;

  if (result.producers) {
    let producerStrings = [];

    result.producers.forEach((producer) => {
      if (producer != undefined) {
        if (producer.prefix) {
          producerStrings.push(producer.prefix + " " + producer.primary_name);
        } else {
          producerStrings.push(producer.primary_name);
        }
      }
    });
    producer = producerStrings.join(", ");
  }

  // get date in same way as original list caption
  let date;
  if (result.date_created != undefined) {
    if (Array.isArray(result.date_created)) {
      date = result.date_created[0];
    } else {
      date = result.date_created;
    }
  }

  return {
    artist: producer,
    culture: result.culture,
    date: date,
    object_number: result.accession_number
  };
}
